import NaoAssociado from 'components/naoAssociado/NaoAssociado';
import RadioSelector from 'components/radioSelector/RadioSelector';
import SideImage from 'components/sideImage/SideImage';
import { RegistroContext } from 'contexts/registroContext/RegistroContext';
import { Ferramenta, ModalidadeParticipante, RotasComBarra } from 'enum';
import {
  Button,
  ButtonThemes,
  CheckCircleIcon,
  Col,
  Input,
  PercentLoaderIcon,
  Row,
  TimesCircleIcon,
} from 'plataforma-fundacao-componentes';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { languageValues } from 'utils/LanguageUtils';
import { formatCPF } from 'utils/MaskUtils';
import { validaCPF } from 'utils/ValidationUtils';
import styles from './Home.scss';
import RecaptchaV2 from 'components/recaptchaV2';

const { rootClassName } = styles;
const { naoEncontrado } = RotasComBarra;

export default function Home() {
  const { hash } = useParams<{ hash: string }>();
  const navigate = useNavigate();
  const {
    searchCPF,
    state,
    dispatch,
    criarParticipanteNaoAssociado,
    isRecaptchaV2,
  } = useContext(RegistroContext);

  const [recaptchaV2Token, setRecaptchaV2Token] = useState<string>('');

  useEffect(() => {
    try {
      const decoded = window.atob(hash!);
      const reg = /^(\d+)\/(\d+)(\/(-|\w)+){0,1}$/;
      const [idFerramenta, idEvento, hibrido] = decoded.split('/');
      if (!reg.test(decoded) || !idFerramenta || !idEvento)
        throw new Error('Parâmetro inválido!');
      dispatch({
        type: 'setFerramenta',
        payload: parseInt(idFerramenta),
      });
      dispatch({
        type: 'setIdEvento',
        payload: parseInt(idEvento),
      });
      dispatch({
        type: 'setHibrido',
        payload: String(hibrido).toLowerCase() === 'hib',
      });
    } catch (err) {
      navigate(naoEncontrado, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, navigate]);

  const [cpf, setCPF] = useState<string>('');

  useEffect(() => {
    dispatch({ type: 'setFlow', payload: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmIsDisabled = useMemo(() => {
    if (isRecaptchaV2 && !recaptchaV2Token) return true;

    if (state.loading || !cpf || !validaCPF(cpf)) return true;

    return (
      (state.ferramenta === Ferramenta.REGISTRO_FORMACAO &&
        !state.modalidade) ||
      (state.naoAssociado &&
        (!state.dadosNaoAssociado?.nome ||
          (state.ferramenta !== Ferramenta.AGENDAMENTO &&
            !state.dadosNaoAssociado?.agencia) ||
          !state.dadosNaoAssociado?.checkTermo))
    );
  }, [
    cpf,
    isRecaptchaV2,
    recaptchaV2Token,
    state.dadosNaoAssociado?.agencia,
    state.dadosNaoAssociado?.checkTermo,
    state.dadosNaoAssociado?.nome,
    state.ferramenta,
    state.loading,
    state.modalidade,
    state.naoAssociado,
  ]);

  const submitForm = useCallback(() => {
    if (state.naoAssociado) {
      criarParticipanteNaoAssociado(
        {
          nome: state.dadosNaoAssociado?.nome!,
          cpf,
          agencia: state.dadosNaoAssociado?.agencia!,
          cooperativa: state.dadosNaoAssociado?.cooperativa,
          idEvento: state.idEvento!,
          system: Ferramenta[state.ferramenta!],
          modalidade: state.modalidade,
        },
        recaptchaV2Token
      );
    } else {
      searchCPF(cpf, recaptchaV2Token);
    }
  }, [
    cpf,
    criarParticipanteNaoAssociado,
    recaptchaV2Token,
    searchCPF,
    state.dadosNaoAssociado?.agencia,
    state.dadosNaoAssociado?.cooperativa,
    state.dadosNaoAssociado?.nome,
    state.ferramenta,
    state.idEvento,
    state.modalidade,
    state.naoAssociado,
  ]);

  return (
    <div className={rootClassName}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        className={`${rootClassName}-form`}
      >
        <Row>
          <Col className='title'>{languageValues.pages.home.title}</Col>
          <Col className='sub-title nunito'>
            {state.ferramenta === Ferramenta.CRESCER
              ? languageValues.pages.naoAssociado.subtitleCrescer
              : languageValues.pages.home.subtitle}
          </Col>
          <Col>
            <Input
              label={languageValues.pages.home.input.label}
              placeholder={languageValues.pages.home.input.placeholder}
              value={cpf}
              onChange={(e) => setCPF(formatCPF(e.target.value))}
              inputMode='numeric'
              disabled={state.naoAssociado}
            />
          </Col>
        </Row>
        {state.ferramenta === Ferramenta.REGISTRO_FORMACAO && (
          <RadioSelector
            label={languageValues.pages.home.perguntaModalidade}
            value={state.modalidade}
            setValue={(payload: ModalidadeParticipante) =>
              dispatch({ type: 'setModalidade', payload })
            }
            options={Object.values(ModalidadeParticipante)
              .filter((m) => {
                return state.hibrido || m !== ModalidadeParticipante.Hibrido;
              })
              .map((m) => {
                return {
                  label: languageValues.buttons.modalidae[m],
                  value: m,
                };
              })}
          />
        )}
        <NaoAssociado
          opened={state.naoAssociado}
          isSistemico={state.eventoSistemico}
        />

        {isRecaptchaV2 && (
          <Row>
            <Col>
              <RecaptchaV2
                onChange={(value) => value && setRecaptchaV2Token(value)}
              />
            </Col>
          </Row>
        )}

        <Row>
          <Col buttonActionsCol end gap={8}>
            {state.naoAssociado && (
              <Button
                type='button'
                theme={ButtonThemes.Secondary}
                leftIcon={<TimesCircleIcon />}
                onClick={() => {
                  setCPF('');
                  dispatch({
                    type: 'setDadosNaoAssociado',
                    payload: { agencia: null, checkTermo: true, nome: '' },
                  });
                  dispatch({ type: 'setModalidade', payload: undefined });
                  dispatch({ type: 'setNaoAssociado', payload: false });
                }}
              >
                {languageValues.buttons.cancel}
              </Button>
            )}
            <Button
              type='button'
              disabled={confirmIsDisabled}
              leftIcon={
                state.loading ? (
                  <PercentLoaderIcon indefinido />
                ) : (
                  <CheckCircleIcon />
                )
              }
              onClick={submitForm}
            >
              {languageValues.buttons.confirm}
            </Button>
          </Col>
        </Row>
      </form>
      <SideImage />
    </div>
  );
}
